import CourseCard from '#/components/common/CourseCard'
import { Button } from '#/components/ui/button'
import coursesDecor from '#/assets/courses/courses-decor.svg'
import coursesWatermark from '#/assets/courses/courses-watermark.png'
import { CgMoreO } from 'react-icons/cg'
import headerIcon from '#/assets/header-icon.svg'
import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { COURSES_CONTENT } from '#/data/homeCourses'
import { Link } from '@tanstack/react-router'
import { useCourses } from '#/features/courses/hooks/useCourses'
import NoData from '#/components/common/NoData'
import courseCardDefault from '#/assets/courses/course-1.png'

export default function CoursesSection() {
  const {
    data: coursesRes,
    isLoading,
    isError,
    refetch,
  } = useCourses({ page: 1, limit: 3 })
  const courses = coursesRes?.data || []

  return (
    <section className="relative z-1 w-full bg-white py-15" id="courses">
      <div className="container mx-auto flex flex-col items-center gap-12.5">
        <img
          alt=""
          className="pointer-events-none z-[-1] absolute left-0 bottom-0"
          src={coursesDecor}
          loading="lazy"
          decoding="async"
        />
        <img
          alt=""
          className="pointer-events-none absolute right-[25.46px] top-3.75 h-31.75 w-[374.542px] opacity-25"
          src={coursesWatermark}
          loading="lazy"
          decoding="async"
          width={375}
          height={127}
        />

        <ScrollReveal className="flex w-full max-w-195 flex-col items-center justify-center gap-3 text-center">
          <RevealItem>
            <img
              alt=""
              src={headerIcon}
              loading="lazy"
              decoding="async"
              width={40}
              height={40}
            />
          </RevealItem>
          <RevealItem>
            <p className="text-[20px] font-heading font-bold leading-[normal] text-primary-900">
              {COURSES_CONTENT.eyebrow}
            </p>
          </RevealItem>
          <RevealItem className="w-full">
            <TextAnimate
              by="word"
              animation="blurInUp"
              as="h2"
              className="text-[60px] font-bold leading-[normal] text-neutral-900 max-md:text-[44px] max-sm:text-[34px]"
            >
              {COURSES_CONTENT.title}
            </TextAnimate>
          </RevealItem>
        </ScrollReveal>

        {isLoading ? (
          <div className="grid w-full grid-cols-1 gap-7.5 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[380px] w-full animate-pulse rounded-xl bg-neutral-100"
              />
            ))}
          </div>
        ) : isError ? (
          <NoData
            title="Could Not Load Courses"
            description="A network error occurred. Please check your connection and try again."
            action={{ label: 'Try Again', onClick: () => refetch() }}
          />
        ) : courses.length === 0 ? (
          <NoData
            title="No Courses Found"
            description="Our curriculum courses will be available soon."
          />
        ) : (
          <ScrollReveal
            className="grid w-full grid-cols-1 gap-7.5 md:grid-cols-2 xl:grid-cols-3"
            stagger={0.1}
          >
            {courses.map((course) => (
              <RevealItem key={course.id}>
                <CourseCard
                  id={course.slug}
                  slug={course.slug}
                  image={course.image || courseCardDefault}
                  tag={course.category?.name}
                  title={course.title}
                  description={
                    course.shortDescription ||
                    course.curriculumDescription ||
                    ''
                  }
                />
              </RevealItem>
            ))}
          </ScrollReveal>
        )}

        <ScrollReveal>
          <RevealItem>
            <Link to={'/courses'}>
              <Button
                className="w-[200px] gap-[8px] px-[20px] py-[14px]"
                variant="outline"
              >
                {COURSES_CONTENT.buttonLabel}
                <CgMoreO className="text-[24px]" />
              </Button>
            </Link>
          </RevealItem>
        </ScrollReveal>
      </div>
    </section>
  )
}
